/**
 * @file 修改密码弹窗
 * @description 旧密码 / 新密码 / 确认密码表单。
 *
 * @whenToUse 顶栏用户菜单「修改密码」。
 * @whenNotToUse 登录页密码输入；忘记密码流程。
 */

import { useState, type SubmitEvent } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { Input } from '@/components/ui/input.tsx'
import { toast } from '@/components/ui/toast.tsx'

export type AdminPasswordDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
}

/**
 * 重置表单字段。
 * @returns 空表单状态
 */
function createEmptyForm() {
  return {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  }
}

/**
 * 修改密码弹窗；校验通过后提示成功（接口待接入）。
 */
export function AdminPasswordDialog({
  open,
  onOpenChange,
}: AdminPasswordDialogProps) {
  const [form, setForm] = useState(createEmptyForm)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  /**
   * 关闭弹窗并清空表单。
   * @param next 是否打开
   */
  function handleOpenChange(next: boolean) {
    onOpenChange(next)
    if (!next) {
      setForm(createEmptyForm())
      setError('')
      setSubmitting(false)
    }
  }

  /**
   * 提交修改密码。
   * @param event 表单提交事件
   */
  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setError('')

    if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
      setError('请填写完整密码信息')
      return
    }

    if (form.newPassword !== form.confirmPassword) {
      setError('两次输入的新密码不一致')
      return
    }

    if (form.newPassword === form.oldPassword) {
      setError('新密码不能与旧密码相同')
      return
    }

    setSubmitting(true)
    toast.success('密码修改成功', '功能待接入后端')
    handleOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title="修改密码"
      footer={
        <>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            取消
          </Button>
          <Button
            type="submit"
            form="admin-password-form"
            loading={submitting}
          >
            确认修改
          </Button>
        </>
      }
    >
      <form
        id="admin-password-form"
        className="space-y-4"
        noValidate
        onSubmit={handleSubmit}
      >
        <FormField label="旧密码" htmlFor="old-password" required>
          <Input
            id="old-password"
            name="oldPassword"
            type="password"
            autoComplete="current-password"
            placeholder="请输入旧密码"
            value={form.oldPassword}
            invalid={Boolean(error)}
            onChange={(event) => {
              setForm((prev) => ({ ...prev, oldPassword: event.target.value }))
              if (error) setError('')
            }}
          />
        </FormField>

        <FormField label="新密码" htmlFor="new-password" required>
          <Input
            id="new-password"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            placeholder="请输入新密码"
            value={form.newPassword}
            invalid={Boolean(error)}
            onChange={(event) => {
              setForm((prev) => ({ ...prev, newPassword: event.target.value }))
              if (error) setError('')
            }}
          />
        </FormField>

        <FormField label="确认密码" htmlFor="confirm-password" required>
          <Input
            id="confirm-password"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            placeholder="请再次输入新密码"
            value={form.confirmPassword}
            invalid={Boolean(error)}
            onChange={(event) => {
              setForm((prev) => ({
                ...prev,
                confirmPassword: event.target.value,
              }))
              if (error) setError('')
            }}
          />
        </FormField>

        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </form>
    </Dialog>
  )
}
